import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatCurrency, relTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Users, LogOut, Zap, Search, ArrowRight, UserCog } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ImpersonationLog } from "@/components/superadmin/ImpersonationLog";
import { Input } from "@/components/ui/input";
import { CreateTenantDialog } from "@/components/superadmin/CreateTenantDialog";

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const { data: companies = [], isLoading } = useQuery({ queryKey: ["sa-companies"], queryFn: () => api.get("/superadmin/companies").then((r) => r.data) });
  const [selectedId, setSelectedId] = useState(null);
  const [q, setQ] = useState("");

  const totals = companies.reduce((acc, c) => ({
    users: acc.users + c.users_count,
    divs: acc.divs + c.divisions_count,
    deals: acc.deals + c.deals_count,
    value: acc.value + c.deals_total_value,
    convs: acc.convs + c.conversations_count,
  }), { users: 0, divs: 0, deals: 0, value: 0, convs: 0 });

  const filtered = companies.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-background grain">
      <div className="border-b border-border py-4 px-6 backdrop-blur-xl bg-background/80 sticky top-0 z-10 flex items-center gap-3">
        <div className="h-8 w-8 rounded-sm bg-primary flex items-center justify-center"><Zap className="h-4 w-4 text-white" /></div>
        <div>
          <div className="font-display font-extrabold">LeadifyCRM · Super Admin</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Cross-company view</div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-right hidden md:block">
            <div className="text-sm font-medium">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{user?.role?.replace("_", " ")}</div>
          </div>
          <button onClick={logout} className="p-2 rounded-sm border border-border hover:bg-secondary/60" data-testid="super-logout" title="Logout"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 lg:p-10">
        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Kpi overline="Platform" label="Companies" value={companies.length} />
          <Kpi overline="Team" label="Users" value={totals.users} />
          <Kpi overline="Workspaces" label="Divisions" value={totals.divs} />
          <Kpi overline="Sales" label="Total Deals" value={totals.deals} />
          <Kpi overline="Pipeline" label="Aggregate Value" value={formatCurrency(totals.value)} highlight />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Company list */}
          <Card className="rounded-sm bg-card border-border p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Tenants</div>
                <div className="font-display text-xl font-extrabold">All Companies</div>
              </div>
              <div className="flex items-center gap-2">
              <CreateTenantDialog onCreated={setSelectedId} />
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input className="rounded-sm bg-secondary/40 pl-9 w-56" placeholder="Search company…" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              </div>
            </div>
            {isLoading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
              <div className="space-y-2">
                {filtered.map((c) => (
                  <div
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedId(c.id)}
                    data-testid="company-row"
                    className={`w-full text-left p-4 border rounded-sm transition-colors ${selectedId === c.id ? "border-primary bg-primary/10" : "border-border bg-secondary/20 hover:border-primary/60"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-sm bg-primary/20 flex items-center justify-center"><Building2 className="h-4 w-4 text-primary" /></div>
                        <div>
                          <div className="font-semibold">{c.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.plan} · {relTime(c.created_at)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <LoginAsButton companyId={c.id} testId={`login-as-${c.id}`} compact />
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-border/40 text-xs">
                      <MiniStat label="Users" value={c.users_count} />
                      <MiniStat label="Divs" value={c.divisions_count} />
                      <MiniStat label="Deals" value={c.deals_count} />
                      <MiniStat label="Value" value={formatCurrency(c.deals_total_value)} />
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No companies match your search.</div>}
              </div>
            )}
          </Card>

          {/* Detail */}
          <div className="lg:col-span-1">
            {selectedId ? <CompanyDetail companyId={selectedId} /> : (
              <Card className="rounded-sm bg-card border-border p-6 text-center text-sm text-muted-foreground">
                Select a company on the left to drill down into its divisions, users, and pipeline.
              </Card>
            )}
          </div>
        </div>
        <ImpersonationLog />
      </div>
    </div>
  );
}

function CompanyDetail({ companyId }) {
  const { data, isLoading } = useQuery({ queryKey: ["sa-company", companyId], queryFn: () => api.get(`/superadmin/companies/${companyId}`).then((r) => r.data) });
  if (isLoading || !data) return <Card className="rounded-sm bg-card border-border p-6"><div className="text-sm text-muted-foreground">Loading…</div></Card>;
  return (
    <div className="space-y-4">
      <Card className="rounded-sm bg-card border-border p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Company</div>
        <div className="font-display text-2xl font-extrabold tracking-tight">{data.company.name}</div>
        <div className="text-xs text-muted-foreground mt-1">{data.company.plan} · created {relTime(data.company.created_at)}</div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <MiniStat label="Deals" value={data.deals_count} />
          <MiniStat label="Won Revenue" value={formatCurrency(data.total_won_value)} />
          <MiniStat label="Pipeline" value={formatCurrency(data.pipeline_value)} />
          <MiniStat label="Users" value={data.users.length} />
        </div>
        <div className="mt-4"><LoginAsButton companyId={companyId} testId="impersonate-tenant-btn" /></div>
      </Card>

      <Card className="rounded-sm bg-card border-border p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Divisions</div>
        <div className="space-y-1">
          {data.divisions.map((d) => (
            <div key={d.id} className="flex items-center justify-between p-2 rounded-sm bg-secondary/30 text-sm">
              <span className="font-medium">{d.name}</span>
              <Badge variant="secondary" className="rounded-sm text-[10px]">{d.currency}</Badge>
            </div>
          ))}
        </div>
      </Card>

      <Card className="rounded-sm bg-card border-border p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Users ({data.users.length})</div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {data.users.map((u) => (
            <div key={u.id} className="flex items-center gap-2 p-2 rounded-sm bg-secondary/20 text-sm">
              <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold">{u.name[0]}</div>
              <div className="flex-1 min-w-0"><div className="truncate">{u.name}</div><div className="text-[10px] text-muted-foreground truncate">{u.email}</div></div>
              <Badge variant="outline" className="rounded-sm text-[10px] capitalize">{u.role?.replace("_", " ")}</Badge>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function LoginAsButton({ companyId, testId, compact }) {
  const { impersonate } = useAuth();
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { await impersonate(companyId); }
    catch (err) { toast.error(err?.response?.data?.detail?.toString?.() || "Could not impersonate"); setBusy(false); }
  };
  return (
    <Button onClick={go} disabled={busy} size={compact ? "sm" : "default"} variant={compact ? "outline" : "default"} className={`rounded-sm ${compact ? "h-7 text-xs" : "w-full"}`} data-testid={testId}>
      <UserCog className={`${compact ? "h-3.5 w-3.5" : "h-4 w-4"} mr-1.5`} /> {busy ? "Opening…" : compact ? "Login as" : "Login as Company Admin"}
    </Button>
  );
}

function Kpi({ overline, label, value, highlight }) {
  return (
    <Card className={`rounded-sm p-5 border-border ${highlight ? "bg-primary/10 border-primary/40" : "bg-card"}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{overline}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-display text-3xl font-extrabold tracking-tight mt-1">{value}</div>
    </Card>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className="font-semibold text-sm mt-0.5">{value}</div>
    </div>
  );
}
